import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Matter from '../../entities/matter';
import Deadline from '../../entities/deadline';
import Invoice from '../../entities/invoice';
import MatterStage from '../../entities/matterStage';
import Transaction from '../../entities/transaction';
import Task from '../../entities/task';
import Document from '../../entities/document';
import Comment from '../../entities/comment';
import Participant from '../../entities/participant';
import RedFlag from '../../entities/redFlag';

/*
 * Delete/nullify anywhere this matter is used in other entities
 *
 * @param {Matter} matter
 */
export function deleteMatter(matter: Matter, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = matter.id;
    }
    /* Simple delete usages */
        
    store.getState().deadlines
        .getMappingFor('matterId', matter.id)
        .filter(deadlineTemp => deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            _deadline.status =  'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;

            _deadline.matterId = '';
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    store.getState().invoices
        .getMappingFor('matterId', matter.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.matterIds = _invoice.matterIds?.filter(id => id !== matter.id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    store.getState().matterStages
        .getMappingFor('matterId', matter.id)
        .filter(matterStageTemp => matterStageTemp.status !== 'D')
        .forEach(matterStageTemp => {
            const _matterStage = matterStageTemp.clone();
            _matterStage.status =  'U';
            _matterStage.addedOn = newHRTimestamp();
            _matterStage.addedBy = getCurrentUserId();
            _matterStage.importSource = targetImportSource;
            _matterStage.importId = mergeId;

            _matterStage.matterId = '';
            store.dispatch(Actions.MatterStageActions.saveMatterStageQueue(_matterStage));
        });

    store.getState().transactions
        .getMappingFor('matterId', matter.id)
        .filter(transactionTemp => transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            _transaction.status =  'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;

            _transaction.matterIds = _transaction.matterIds?.filter(id => id !== matter.id) || [];
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* Complex delete usages */
        /* - Delete key/value usages */
    
    findAll<Transaction>('transactions')
        .filter(transactionTemp => transactionTemp.sourceType === 'M' && transactionTemp.source === matter.id && transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            // Update the target with a null in the field (do NOT delete!)
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    findAll<Transaction>('transactions')
        .filter(transactionTemp => transactionTemp.targetType === 'M' && transactionTemp.target === matter.id && transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            // Update the target with a null in the field (do NOT delete!)
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* - Delete embedded value usages */
    
    findAll<Transaction>('transactions')
        // Has to have the paymentDistribution property
        .filter(transactionTemp => transactionTemp.status !== 'D' && transactionTemp.hasOwnProperty('paymentDistribution'))
        // Has to have a paymentDistribution with type transaction
        .filter(transactionTemp => !!transactionTemp.paymentDistribution?.some(item => item.matterId === matter.id))
        // For each transaction, go through paymentDistribution and delete entries with the matching value
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            if (!!transactionTemp.paymentDistribution) {
            	_transaction.paymentDistribution = [...transactionTemp.paymentDistribution]
                	.filter(f => f.matterId !== matter.id);
            }
            else {
            	_transaction.paymentDistribution = [];
            }
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* - Delete embedded key/value usages */
    
    findAll<Task>('tasks')
        // Has to have the tieIns property
        .filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns'))
        // Has to have a tieIns with a key/value pair that matches
        .filter(taskTemp => !!taskTemp.tieIns?.some(f => {
            return 'Matter' === f.entity && f.id === matter.id;
        }))
        // For each task, go through tieIns and delete entries with the key/value pair
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.tieIns = [...(taskTemp.tieIns || [])]
                .filter(f => !('Matter' === f.entity && f.id === matter.id));
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    /* Remove the actual matter */
    matter.status = 'D';
    matter.addedOn = newHRTimestamp();
    matter.addedBy = getCurrentUserId();
    matter.importSource = targetImportSource;
    matter.importId = mergeId;
    store.dispatch(Actions.MatterActions.removeMatterQueue(matter));
    

    /* Delete any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'matter' && documentTemp.entityId === matter.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Delete the target
            _document.status = 'D';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.removeDocumentQueue(_document));
         });
    

    /* Delete any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'matter' && commentTemp.entityId === matter.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Delete the target
            _comment.status = 'D';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.removeCommentQueue(_comment));
         });
    

    /* Delete any participants */
    findAll<Participant>('participants')
         .filter(participantTemp => participantTemp.entityType === 'matter' && participantTemp.entityId === matter.id && participantTemp.status !== 'D')
         .forEach(participantTemp => {
            const _participant = participantTemp.clone();
            // Delete the target
            _participant.status = 'D';
            _participant.addedOn = newHRTimestamp();
            _participant.addedBy = getCurrentUserId();
            _participant.importSource = targetImportSource;
            _participant.importId = mergeId;
            store.dispatch(Actions.ParticipantActions.removeParticipantQueue(_participant));
         });
    

    /* Delete any redFlags */
    findAll<RedFlag>('redFlags')
         .filter(redFlagTemp => redFlagTemp.entityType === 'matter' && redFlagTemp.entityId === matter.id && redFlagTemp.status !== 'D')
         .forEach(redFlagTemp => {
            const _redFlag = redFlagTemp.clone();
            // Delete the target
            _redFlag.status = 'D';
            _redFlag.addedOn = newHRTimestamp();
            _redFlag.addedBy = getCurrentUserId();
            _redFlag.importSource = targetImportSource;
            _redFlag.importId = mergeId;
            store.dispatch(Actions.RedFlagActions.removeRedFlagQueue(_redFlag));
         });
    
}

/*
 * Replace anywhere this matter is used in other entities.
 *
 * @param {Matter} matter
 * @param {Matter} replaceWith
 * @return {Matter} - Matter will have refs stripped away
 */
export function replaceMatter(matter: Matter, replaceWith: Matter, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = matter.id;
    }
    /* Simple replace usages */
        
    store.getState().deadlines
        .getMappingFor('matterId', matter.id)
        .filter(deadlineTemp => deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            _deadline.status =  'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;

            _deadline.matterId = replaceWith.id;
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    store.getState().invoices
        .getMappingFor('matterId', matter.id)
        .filter(invoiceTemp => invoiceTemp.status !== 'D')
        .forEach(invoiceTemp => {
            const _invoice = invoiceTemp.clone();
            _invoice.status =  'U';
            _invoice.addedOn = newHRTimestamp();
            _invoice.importSource = targetImportSource;
            _invoice.importId = mergeId;

            _invoice.matterIds = _invoice.matterIds?.map(id => (id === matter.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
        });

    store.getState().matterStages
        .getMappingFor('matterId', matter.id)
        .filter(matterStageTemp => matterStageTemp.status !== 'D')
        .forEach(matterStageTemp => {
            const _matterStage = matterStageTemp.clone();
            _matterStage.status =  'U';
            _matterStage.addedOn = newHRTimestamp();
            _matterStage.addedBy = getCurrentUserId();
            _matterStage.importSource = targetImportSource;
            _matterStage.importId = mergeId;

            _matterStage.matterId = replaceWith.id;
            store.dispatch(Actions.MatterStageActions.saveMatterStageQueue(_matterStage));
        });

    store.getState().transactions
        .getMappingFor('matterId', matter.id)
        .filter(transactionTemp => transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            _transaction.status =  'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;

            _transaction.matterIds = _transaction.matterIds?.map(id => (id === matter.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* Complex replace usages */
        /* - Modify key/value usages */
    
    findAll<Transaction>('transactions')
        .filter(transactionTemp => transactionTemp.sourceType === 'M' && transactionTemp.source === matter.id && transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            // Replace the target with the new ID
			_transaction.source = replaceWith.id;
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    findAll<Transaction>('transactions')
        .filter(transactionTemp => transactionTemp.targetType === 'M' && transactionTemp.target === matter.id && transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            // Replace the target with the new ID
			_transaction.target = replaceWith.id;
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* - Modify embedded value usages */
    
    findAll<Transaction>('transactions')
        // Has to have the paymentDistribution property
        .filter(transactionTemp => transactionTemp.status !== 'D' && transactionTemp.hasOwnProperty('paymentDistribution'))
        // Has to have a paymentDistribution with type transaction
        .filter(transactionTemp => !!transactionTemp.paymentDistribution?.some(item => item.matterId === matter.id))
        // For each transaction, go through paymentDistribution and replace entries with the matching value
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            if (!!transactionTemp.paymentDistribution) {
            	_transaction.paymentDistribution = [...transactionTemp.paymentDistribution]
                	.map(f => {
	                    if(f.matterId === matter.id) {
	                        const _f = {...f};
	                        _f.matterId = replaceWith.id;
	                        return _f;
	                    }
	                    else return f;
	                });
            }
            else {
            	_transaction.paymentDistribution = [];
            }
            _transaction.status = 'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* - Modify embedded key/value usages */
    
    findAll<Task>('tasks')
        // Has to have the tieIns property
        .filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns'))
        // Has to have a tieIns with a key/value pair that matches
        .filter(taskTemp => !!taskTemp.tieIns?.some(f => {
            return 'Matter' === f.entity && f.id === matter.id;
        }))
        // For each task, go through tieIns and replace entries with the key/value pair
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.tieIns = [...(taskTemp.tieIns || [])]
                .map(f => {
                        if('Matter' === f.entity && f.id === matter.id) {
                            const _f = {...f};
                            _f.id = replaceWith.id;
                            return _f;
                        }
                        else return f;
                    });
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    /* Move any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'matter' && documentTemp.entityId === matter.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Replace the target with the new ID
			_document.entityId = replaceWith.id;
            _document.status = 'U';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
         });
    

    /* Move any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'matter' && commentTemp.entityId === matter.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Replace the target with the new ID
			_comment.entityId = replaceWith.id;
            _comment.status = 'U';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
         });
    

    /* Move any participants */
    findAll<Participant>('participants')
         .filter(participantTemp => participantTemp.entityType === 'matter' && participantTemp.entityId === matter.id && participantTemp.status !== 'D')
         .forEach(participantTemp => {
            const _participant = participantTemp.clone();
            // Replace the target with the new ID
			_participant.entityId = replaceWith.id;
            _participant.status = 'U';
            _participant.addedOn = newHRTimestamp();
            _participant.addedBy = getCurrentUserId();
            _participant.importSource = targetImportSource;
            _participant.importId = mergeId;
            store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
         });
    

    /* Move any redFlags */
    findAll<RedFlag>('redFlags')
         .filter(redFlagTemp => redFlagTemp.entityType === 'matter' && redFlagTemp.entityId === matter.id && redFlagTemp.status !== 'D')
         .forEach(redFlagTemp => {
            const _redFlag = redFlagTemp.clone();
            // Replace the target with the new ID
			_redFlag.entityId = replaceWith.id;
            _redFlag.status = 'U';
            _redFlag.addedOn = newHRTimestamp();
            _redFlag.addedBy = getCurrentUserId();
            _redFlag.importSource = targetImportSource;
            _redFlag.importId = mergeId;
            store.dispatch(Actions.RedFlagActions.saveRedFlagQueue(_redFlag));
         });
    
    return matter;
}

/*
 * Determine the impact of deleting this matter from other entities
 *
 * @param {Matter} matter
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactMatter(matter: Matter) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const deadlineMatterIdImpact = state.deadlines
          .getMappingFor('matterId', matter.id)
          .filter(deadline => deadline.status !== 'D');

    deadlineMatterIdImpact.forEach(() => impacts.push('deadlines*'));

    const invoiceMatterIdsImpact = state.invoices
          .getMappingFor('matterId', matter.id)
          .filter(invoice => invoice.status !== 'D');

    invoiceMatterIdsImpact.forEach(() => impacts.push('invoices'));

    const matterStageMatterIdImpact = state.matterStages
          .getMappingFor('matterId', matter.id)
          .filter(matterStage => matterStage.status !== 'D');

    matterStageMatterIdImpact.forEach(() => impacts.push('matterStages*'));

    const transactionMatterIdsImpact = state.transactions
          .getMappingFor('matterId', matter.id)
          .filter(transaction => transaction.status !== 'D');

    transactionMatterIdsImpact.forEach(() => impacts.push('transactions*'));

    /* Complex delete usages */
        /* - Impact key/value usages */
    
    const transactionSourceTypeImpact = findAll<Transaction>('transactions')
        .filter(transaction => transaction.sourceType === 'M' && transaction.source === matter.id && transaction.status !== 'D');
    transactionSourceTypeImpact.forEach(() => impacts.push('transactions'));

    const transactionTargetTypeImpact = findAll<Transaction>('transactions')
        .filter(transaction => transaction.targetType === 'M' && transaction.target === matter.id && transaction.status !== 'D');
    transactionTargetTypeImpact.forEach(() => impacts.push('transactions*'));

    /* - Impact embedded value usages */
    
    const transactionPaymentDistributionImpact = findAll<Transaction>('transactions')
		.filter(transaction => transaction.status !== 'D' && transaction.hasOwnProperty('paymentDistribution'))
		.filter(transaction => !!transaction.paymentDistribution?.some(item => item.matterId === matter.id));
    transactionPaymentDistributionImpact.forEach(() => impacts.push('transactions'));

    /* - Impact embedded key/value usages */
    
    const taskTieInsImpact = findAll<Task>('tasks')
		.filter(task => task.status !== 'D' && task.hasOwnProperty('tieIns'))
		.filter(task => !!task.tieIns?.some(f => {
				return 'Matter' === f.entity && f.id === matter.id;
			}));
    taskTieInsImpact.forEach(() => impacts.push('tasks'));

    return util.countDistinct(impacts);
}